/**
 * A integração de ENTRADA com a folha de pagamento.
 *
 * O contrário da aba "Integrações", que guarda as credenciais com que este sistema chama
 * fornecedores. Aqui a empresa emite uma chave para que o sistema de folha DELA escreva o
 * cadastro de funcionários daqui — admissão e demissão deixam de ser digitadas duas vezes.
 *
 * O momento que define esta tela é o da criação: a chave aparece uma única vez e não há como
 * mostrá-la de novo (o banco guarda só o resumo). Por isso o diálogo de criação entrega, junto
 * da chave, a URL e o exemplo de chamada — é esse bloco inteiro que a pessoa manda para o
 * fornecedor da folha, e é o que faz a integração acontecer ou não sair do lugar.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  PlayCircleOutline as TestarIcon,
  SyncAlt as SyncAltIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { ProfileSection } from '../../../../components/common';
import { useAcao } from '../../../../authorization/useVeredito';
import { payrollIntegrationService } from '../../../../services/payrollIntegrationService';
import {
  PAYROLL_PROVIDERS,
  PayrollConnection,
} from '../../../../types/payrollIntegration';
import PayrollSyncRunsDialog from './PayrollSyncRunsDialog';

interface CompanyPayrollIntegrationTabProps {
  companyId: string;
}

/** O caminho publicado. Fixo no código porque é contrato, não configuração de tela. */
const CAMINHO_DA_ROTA = '/externo/v1/folha/funcionarios';

/**
 * A origem de onde a API responde — quase nunca a mesma do frontend.
 *
 * Em dev o Vite serve a tela numa porta e o backend responde em outra (`VITE_API_URL` aponta
 * pra isso); usar `window.location.origin` aqui mostraria ao cliente uma URL da PÁGINA, que a
 * folha jamais alcançaria. Em produção, atrás de um proxy reverso, `VITE_API_URL` costuma ser
 * relativo (`/api/v1`) — aí sim a origem da página é a certa, e é o que o fallback cobre.
 */
function origemDaApi(): string {
  const configurado = String(import.meta.env['VITE_API_URL'] ?? '');
  try {
    return new URL(configurado).origin;
  } catch {
    return window.location.origin.replace(/\/+$/, '');
  }
}

function urlDaIntegracao(): string {
  return `${origemDaApi()}${CAMINHO_DA_ROTA}`;
}

function exemploDeChamada(chave: string): string {
  return [
    `curl -X POST ${urlDaIntegracao()} \\`,
    `  -H "Authorization: Bearer ${chave}" \\`,
    '  -H "Content-Type: application/json" \\',
    `  -d '{"funcionarios":[{"cpf":"52998224725","name":"Maria Souza",`,
    `       "admissionDate":"2026-03-01","status":"ATIVO",`,
    `       "department":"Produção","occupation":"Operadora"}]}'`,
  ].join('\n');
}

function descreverUltimoUso(valor: string | null): string {
  if (!valor) return 'Nunca usada';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return 'Nunca usada';
  return `Última carga em ${data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`;
}

const CompanyPayrollIntegrationTab: React.FC<CompanyPayrollIntegrationTabProps> = ({
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const acao = useAcao('company.manage-payroll-integration', 'Gerenciar integração com a folha');

  const [conexoes, setConexoes] = useState<PayrollConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [criando, setCriando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [nome, setNome] = useState('');
  const [provider, setProvider] = useState('generico');
  const [criarEstrutura, setCriarEstrutura] = useState(false);

  // A chave em claro vive só enquanto este diálogo está aberto — fechá-lo a descarta.
  const [chaveRecemCriada, setChaveRecemCriada] = useState<string | null>(null);

  const [historicoDe, setHistoricoDe] = useState<PayrollConnection | null>(null);
  const [excluindo, setExcluindo] = useState<PayrollConnection | null>(null);
  const [alterando, setAlterando] = useState<string | null>(null);
  const [testando, setTestando] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setConexoes(await payrollIntegrationService.list(companyId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar as conexões');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  function abrirCriacao(): void {
    setNome('');
    setProvider('generico');
    setCriarEstrutura(false);
    setCriando(true);
  }

  async function handleCriar(): Promise<void> {
    if (!nome.trim()) {
      enqueueSnackbar('Dê um nome para reconhecer esta conexão', { variant: 'warning' });
      return;
    }

    setSalvando(true);
    try {
      const criada = await payrollIntegrationService.create(companyId, {
        name: nome.trim(),
        provider,
        createMissingStructure: criarEstrutura,
      });

      setConexoes((anteriores) => [criada.connection, ...anteriores]);
      setCriando(false);
      setChaveRecemCriada(criada.chave);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao criar a conexão', {
        variant: 'error',
      });
    } finally {
      setSalvando(false);
    }
  }

  async function handleAlternarAtiva(conexao: PayrollConnection): Promise<void> {
    setAlterando(conexao.id);
    try {
      const atualizada = await payrollIntegrationService.update(companyId, conexao.id, {
        active: !conexao.active,
      });
      setConexoes((anteriores) =>
        anteriores.map((item) => (item.id === conexao.id ? atualizada : item))
      );
      enqueueSnackbar(atualizada.active ? 'Conexão reativada' : 'Conexão desligada', {
        variant: 'success',
      });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao alterar a conexão', {
        variant: 'error',
      });
    } finally {
      setAlterando(null);
    }
  }

  async function handleAlternarEstrutura(conexao: PayrollConnection): Promise<void> {
    setAlterando(conexao.id);
    try {
      const atualizada = await payrollIntegrationService.update(companyId, conexao.id, {
        createMissingStructure: !conexao.createMissingStructure,
      });
      setConexoes((anteriores) =>
        anteriores.map((item) => (item.id === conexao.id ? atualizada : item))
      );
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao alterar a conexão', {
        variant: 'error',
      });
    } finally {
      setAlterando(null);
    }
  }

  /**
   * Simula uma admissão de mentira por dentro do backend — sem terminal, sem precisar da chave
   * em claro. Prova que o lado do Lidera SST está pronto; o que acontece dentro do sistema de
   * folha continua fora do nosso alcance.
   */
  async function handleTestar(conexao: PayrollConnection): Promise<void> {
    setTestando(conexao.id);
    try {
      const resultado = await payrollIntegrationService.test(companyId, conexao.id);
      enqueueSnackbar(resultado.message, { variant: resultado.ok ? 'success' : 'warning' });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao testar a conexão', {
        variant: 'error',
      });
    } finally {
      setTestando(null);
    }
  }

  async function handleExcluir(): Promise<void> {
    if (!excluindo) return;
    const alvo = excluindo;
    setAlterando(alvo.id);
    try {
      await payrollIntegrationService.remove(companyId, alvo.id);
      setConexoes((anteriores) => anteriores.filter((item) => item.id !== alvo.id));
      enqueueSnackbar('Conexão excluída. O histórico de sincronizações foi mantido.', {
        variant: 'success',
      });
      setExcluindo(null);
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao excluir a conexão', {
        variant: 'error',
      });
    } finally {
      setAlterando(null);
    }
  }

  async function copiar(texto: string, oQue: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(texto);
      enqueueSnackbar(`${oQue} copiado`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Não foi possível copiar. Selecione e copie manualmente.', {
        variant: 'warning',
      });
    }
  }

  return (
    <ProfileSection title="Integração com a folha" icon={<SyncAltIcon />}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Emita uma chave para que o sistema de folha de pagamento desta empresa envie admissões e
        demissões automaticamente, sem digitação.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {conexoes.length === 0 ? (
            <Alert severity="info">
              <AlertTitle>Nenhuma conexão emitida</AlertTitle>
              Ao criar uma conexão, você recebe uma chave para entregar a quem administra a folha
              de pagamento. É com ela que a folha passa a enviar as admissões e demissões.
            </Alert>
          ) : (
            <Stack
              divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}
            >
              {conexoes.map((conexao) => (
                <Stack
                  key={conexao.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                  sx={{ py: 2 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {conexao.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={conexao.active ? 'Ativa' : 'Desligada'}
                        color={conexao.active ? 'success' : 'default'}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {conexao.keyPrefix}… · {descreverUltimoUso(conexao.lastUsedAt)}
                    </Typography>
                    <FormControlLabel
                      sx={{ mt: 0.5 }}
                      control={
                        <Switch
                          size="small"
                          checked={conexao.createMissingStructure}
                          disabled={acao.disabled || alterando === conexao.id}
                          onChange={() => void handleAlternarEstrutura(conexao)}
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          {conexao.createMissingStructure
                            ? 'Cria setor e cargo que a folha mandar e não existirem aqui'
                            : 'Recusa a linha se o setor ou o cargo não existirem aqui'}
                        </Typography>
                      }
                    />
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip
                      title={
                        conexao.active
                          ? 'Testar conexão — cria e remove um funcionário de teste, sem terminal'
                          : 'Reative a conexão para poder testar'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={!conexao.active || testando === conexao.id}
                          onClick={() => void handleTestar(conexao)}
                        >
                          <TestarIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Ver sincronizações recebidas">
                      <span>
                        <IconButton size="small" onClick={() => setHistoricoDe(conexao)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={acao.tooltip ?? (conexao.active ? 'Desligar' : 'Reativar')}>
                      <span>
                        <Switch
                          size="small"
                          color="success"
                          checked={conexao.active}
                          disabled={acao.disabled || alterando === conexao.id}
                          onChange={() => void handleAlternarAtiva(conexao)}
                        />
                      </span>
                    </Tooltip>

                    <Tooltip title={acao.tooltip ?? 'Excluir conexão'}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={acao.disabled || alterando === conexao.id}
                          onClick={() => setExcluindo(conexao)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          <Box>
            <Tooltip title={acao.tooltip ?? ''}>
              <span>
                <Button variant="outlined" disabled={acao.disabled} onClick={abrirCriacao}>
                  Criar conexão
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      )}

      {/* Criação */}
      <Dialog open={criando} onClose={() => !salvando && setCriando(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Criar conexão com a folha</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Nome da conexão"
              placeholder="Ex.: Senior HCM da matriz"
              helperText="Como sua equipe reconhece esta conexão na lista"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
              autoFocus
            />

            <TextField
              select
              label="Sistema de folha"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              helperText="Só identifica a origem no histórico — o formato de envio é o mesmo para todos"
              fullWidth
            >
              {PAYROLL_PROVIDERS.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={criarEstrutura}
                    onChange={(e) => setCriarEstrutura(e.target.checked)}
                  />
                }
                label="Criar setor e cargo automaticamente"
              />
              <Typography variant="caption" color="text.secondary" component="div">
                {criarEstrutura
                  ? 'Se a folha mandar um setor ou cargo que ainda não existe aqui, ele será criado.'
                  : 'Se a folha mandar um setor ou cargo que não existe aqui, aquela linha será recusada e o motivo aparecerá no histórico.'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriando(false)} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleCriar()} disabled={salvando}>
            {salvando ? 'Criando...' : 'Criar e gerar chave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* A chave, uma vez só */}
      <Dialog
        open={chaveRecemCriada !== null}
        onClose={() => setChaveRecemCriada(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Chave criada</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Esta é a única vez que a chave aparece</AlertTitle>
            Copie agora e guarde em local seguro. Se perder, será preciso criar outra conexão —
            não há como recuperá-la.
          </Alert>

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Chave de acesso
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                A senha que identifica esta empresa. Quem for configurar o sistema de folha vai
                precisar dela.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  value={chaveRecemCriada ?? ''}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Tooltip title="Copiar chave">
                  <IconButton onClick={() => void copiar(chaveRecemCriada ?? '', 'Chave')}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Endereço para onde o sistema de folha deve enviar os dados
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Não é uma página para visitar no navegador — é o endereço que alguém cola na
                tela de integrações do Senior, TOTVS ou outro sistema de folha, junto da chave
                acima.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  value={urlDaIntegracao()}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Tooltip title="Copiar endereço">
                  <IconButton onClick={() => void copiar(urlDaIntegracao(), 'Endereço')}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Exemplo pronto para repassar
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Isto não precisa ser rodado por você — é o modelo que quem configura o sistema
                de folha usa como referência do formato esperado.
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  overflowX: 'auto',
                  whiteSpace: 'pre',
                  m: 0,
                }}
              >
                {exemploDeChamada(chaveRecemCriada ?? '')}
              </Box>
              <Button
                size="small"
                startIcon={<CopyIcon fontSize="small" />}
                sx={{ mt: 1 }}
                onClick={() => void copiar(exemploDeChamada(chaveRecemCriada ?? ''), 'Exemplo')}
              >
                Copiar exemplo
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setChaveRecemCriada(null)}>
            Já copiei a chave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exclusão */}
      <Dialog open={excluindo !== null} onClose={() => setExcluindo(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir a conexão?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A chave de <strong>{excluindo?.name}</strong> para de funcionar imediatamente, e a
            folha deixa de conseguir enviar admissões e demissões por ela. O histórico de
            sincronizações já recebidas continua disponível.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExcluindo(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void handleExcluir()}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <PayrollSyncRunsDialog
        open={historicoDe !== null}
        companyId={companyId}
        connection={historicoDe}
        onClose={() => setHistoricoDe(null)}
      />
    </ProfileSection>
  );
};

export default CompanyPayrollIntegrationTab;
